import type { CustomizeFieldsSettings } from '../types';

const STORAGE_KEY_PREFIX = 'customize-fields-';
const VERSION = '1'; // 用于数据迁移

export function useFieldsStorage() {
  const storage = window.localStorage;

  // 获取用户特定的存储前缀
  const getUserStoragePrefix = (): string => {
    const userId = getUserId();
    // 添加用户ID和IsVendor标志，确保不同账号和不同角色有不同的键
    return `${userId}_${localStorage.getItem('IsVendor') || '0'}_`;
  };

  // 获取存储键
  const getStorageKey = (pageType: string): string => {
    const userPrefix = getUserStoragePrefix();
    return `${userPrefix}${STORAGE_KEY_PREFIX}${pageType}-v${VERSION}`;
  };

  // 获取字段可见性存储键
  const getVisibilityKey = (): string => {
    const userId = getUserId();
    return `${userId}_${localStorage.getItem('IsVendor') || '0'}_shipments_fields_visibility`;
  };

  // 检查存储是否可用
  const isStorageAvailable = (): boolean => {
    try {
      const testKey = 'storage-test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  };

  // 尝试从旧版本的存储键迁移数据
  const migrateFromOldVersion = async (pageType: string): Promise<CustomizeFieldsSettings | null> => {
    try {
      const oldKey = `${STORAGE_KEY_PREFIX}${pageType}`;
      const oldData = localStorage.getItem(oldKey);
      if (!oldData) return null;

      const oldSettings = JSON.parse(oldData) as CustomizeFieldsSettings;

      // 保存到新版本并删除旧数据
      await saveSettings(oldSettings);
      localStorage.removeItem(oldKey);

      return oldSettings;
    } catch (e) {
      return null;
    }
  };

  const getSettings = async (pageType: string): Promise<CustomizeFieldsSettings | null> => {
    try {
      // 使用新的键生成方式
      const key = getStorageKey(pageType);
      console.log('[DEBUG-STORAGE] Getting settings with key:', key);
      const settings = storage.getItem(key);
      
      if (!settings) {
        console.log('[DEBUG-STORAGE] No settings found for key:', key);
        return null;
      }
      
      const parsedSettings = JSON.parse(settings) as CustomizeFieldsSettings;
      console.log('[DEBUG-STORAGE] Retrieved settings:', parsedSettings);
      return parsedSettings;
    } catch (e) {
      console.error('[DEBUG-STORAGE] Error getting settings:', e);
      return null;
    }
  };

  const saveSettings = async (settings: CustomizeFieldsSettings): Promise<void> => {
    try {
      // 使用新的键生成方式
      const key = getStorageKey(settings.pageType);
      console.log('[DEBUG-STORAGE] Saving settings with key:', key);
      storage.setItem(key, JSON.stringify(settings));
      
      // 同时更新常规字段可见性存储
      const visibilityKey = getVisibilityKey();
      const visibilityData = Object.entries(settings.fields).reduce((acc, [key, field]) => {
        acc[key] = field.visible;
        return acc;
      }, {} as Record<string, boolean>);
      
      console.log('[DEBUG-STORAGE] Updating visibility data with key:', visibilityKey);
      storage.setItem(visibilityKey, JSON.stringify(visibilityData));
      console.log('[DEBUG-STORAGE] Settings saved successfully');
    } catch (e) {
      console.error('[DEBUG-STORAGE] Error saving settings:', e);
      throw e;
    }
  };

  // 获取当前用户ID
  const getUserId = (): string => {
    // 从应用上下文获取用户ID，如果不可用则使用默认值
    try {
      // 优先获取当前用户ID
      const userID = localStorage.getItem('UserID');
      if (userID) {
        console.log('[DEBUG-STORAGE] Using UserID from localStorage:', userID);
        return userID;
      }
      
      // 尝试从store获取
      // @ts-ignore - 假设Vue实例可能在window上
      const store = window.$store || (window.Vue && window.Vue.prototype.$store);
      if (store && store.state && store.state.user && store.state.user.id) {
        console.log('[DEBUG-STORAGE] Using user ID from store:', store.state.user.id);
        return store.state.user.id;
      }
    } catch (e) {
      console.error('[DEBUG-STORAGE] Error getting user ID:', e);
    }
    
    // 获取账号的唯一标识符
    const vendorFlag = localStorage.getItem('IsVendor') || '0';
    const uniqueID = `user-${vendorFlag}`;
    console.log('[DEBUG-STORAGE] Using generated unique ID:', uniqueID);
    return uniqueID;
  };

  return {
    getSettings,
    saveSettings
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./src/layouts/shipments-4-shippers/components/CustomizeFieldsPanel/composables/useFieldsStorage.ts